/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, VisuallyHidden, breakpoints } from '@coursera/cds-core';

import { CDS_GRID_LARGE_PADDING, CDS_GRID_PADDING } from 'bundles/cds-labs/components/OverflowCarousel/constants';

import _t from 'i18n!nls/cds-labs';

const styles = {
  container: css`
    position: absolute;
    list-style: none;
    padding: 0 ${CDS_GRID_PADDING}px;
    margin: 0;
    left: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: var(--cds-spacing-100);
  `,
  containerMedium: css`
    ${breakpoints.only('md')} {
      padding-left: ${CDS_GRID_LARGE_PADDING}px;
    }
  `,
  item: css`
    display: flex;
    align-items: center;
    justify-content: center;
  `,
  button: css`
    padding: 0;
  `,
  inViewIndicator: css`
    width: 20px;
    height: 8px;
    border-radius: var(--cds-border-radius-100);
    background-color: var(--cds-color-neutral-primary-weak);

    &:focus,
    &:focus-visible,
    &:hover {
      background-color: var(--cds-color-neutral-primary-weak);
    }

    &:hover {
      box-shadow: 0 0 0 1px var(--cds-color-neutral-primary-weak);
    }
  `,
  notInViewIndicator: css`
    width: 8px;
    height: 8px;
    border-radius: var(--cds-border-radius-100);
    background-color: var(--cds-color-neutral-disabled-strong);

    &:focus,
    &:focus-visible,
    &:hover {
      background-color: var(--cds-color-neutral-disabled-strong);
    }

    &:hover {
      box-shadow: 0 0 0 1px var(--cds-color-neutral-disabled-strong);
    }
  `,
  ctaAfterNavigationButtonsContainer: css`
    bottom: calc(var(--cds-spacing-150) * -1);

    li > a {
      margin-left: var(--cds-spacing-150);
    }
  `,
};

type Props = {
  numberOfItems: number;
  navigateToItem?: (index: number) => () => void;
  itemsInView: string[];
  // when true, the navigation buttons will be indented to align with the CDS grid at the medium breakpoint.
  indentToAlignWithGridAtMediumBreakpoint?: boolean;
  // when provided, this button will render after the navigation buttons.
  ctaAfterNavigationButtons?: React.ReactNode;
};

const NavigationButtons = ({
  numberOfItems,
  navigateToItem,
  itemsInView,
  indentToAlignWithGridAtMediumBreakpoint,
  ctaAfterNavigationButtons,
}: Props) => {
  if (numberOfItems <= 1) {
    return null;
  }

  return (
    <ul
      css={[
        styles.container,
        indentToAlignWithGridAtMediumBreakpoint && styles.containerMedium,
        ctaAfterNavigationButtons && styles.ctaAfterNavigationButtonsContainer,
      ]}
    >
      {Array.from(Array(numberOfItems).keys()).map((value) => (
        <li key={value} css={styles.item}>
          <Button
            variant="ghost"
            size="small"
            onClick={navigateToItem?.(value)}
            css={[
              styles.button,
              itemsInView.includes(value.toString()) ? styles.inViewIndicator : styles.notInViewIndicator,
            ]}
          >
            <VisuallyHidden>{_t('Go to item #{index}', { index: value + 1 })}</VisuallyHidden>
          </Button>
        </li>
      ))}
      {ctaAfterNavigationButtons && (
        <li key="cta-after-nav-buttons" css={styles.item}>
          {ctaAfterNavigationButtons}
        </li>
      )}
    </ul>
  );
};

export default NavigationButtons;
